import { type RefObject, useEffect, useRef } from "react";

/**
 * Options for the useInfiniteScroll hook
 */
interface UseInfiniteScrollOptions {
  /** Whether there are more pages to fetch */
  hasNextPage: boolean;
  /** Whether a page is currently being fetched */
  isFetchingNextPage: boolean;
  /** Function to call to fetch the next page */
  fetchNextPage: () => void;
  /** Intersection threshold (0-1) for triggering the next page fetch */
  threshold?: number;
  /** Root margin for the intersection observer */
  rootMargin?: string;
  /** Whether the infinite scroll is enabled */
  enabled?: boolean;
}

/**
 * Hook for implementing infinite scroll using IntersectionObserver.
 * Returns a ref that should be attached to the trigger element
 * (typically placed at the bottom of the scrollable container).
 *
 * @example
 * ```tsx
 * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(...);
 * const loadMoreRef = useInfiniteScroll({
 *   hasNextPage,
 *   isFetchingNextPage,
 *   fetchNextPage,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} />)}
 *     <div ref={loadMoreRef} />
 *   </div>
 * );
 * ```
 */
export function useInfiniteScroll({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  threshold = 0.1,
  rootMargin = "0px",
  enabled = true,
}: UseInfiniteScrollOptions): RefObject<HTMLDivElement> {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !triggerRef.current) return;

    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    // Start observing
    observerRef.current.observe(triggerRef.current);

    // Cleanup on unmount or dependency change
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    threshold,
    rootMargin,
    enabled,
  ]);

  return triggerRef;
}
